import React, { useEffect, useMemo, useState } from 'react';
import {
  ScrollView,
  View,
  Text,
  TouchableHighlight,
  TouchableOpacity,
  Dimensions,
  Image,
} from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';

import { getDiscountCodesData, deleteDiscountCode, getDiscountCodesList } from '../../../api/Settings';
import { getBrandsData } from '../../../api/Price';
import { msgStr } from '../../../common/constants/Message';
import { TextdefaultSize, TextMediumSize } from '../../../common/constants/Fonts';
import { useAlertModal, useConfirmModal } from '../../../common/hooks';
import BasicLayout from '../../../common/components/CustomLayout/BasicLayout';
import { BOHTlbrSearchPicker } from '../../../common/components/bohtoolbar';

import { discountCodesStyle } from './styles/DiscountCodesStyle';
import AddDiscountCodeModal from './AddDiscountCodeModal';
import QuickAddDiscountCodeModal from './QuickAddDiscountCodeModal';

const DiscountCodes = ({ navigation, openInventory }) => {
  const screenHeight = Dimensions.get('window').height;

  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();

  const [tableData, setTableData] = useState([]);
  const [brandData, setBrandData] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [selectedActive, setSelectedActive] = useState<string | null>(null);
  const [updateDiscountCodeTrigger, setUpdateDiscountCodeTrigger] = useState(true);

  const [isAddModalVisible, setAddModalVisible] = useState(false);
  const [selectedDiscountCode, setSelectedDiscountCode] = useState(null);
  const openAddDiscountCodeModal = () => {
    setAddModalVisible(true);
    setSelectedDiscountCode(null);
  };
  const closeAddDiscountCodeModal = () => {
    setAddModalVisible(false);
    setSelectedDiscountCode(null);
  };
  const editDiscountCode = (item) => {
    setSelectedDiscountCode(item);
    setAddModalVisible(true);
  };

  const [isQuickAddModalVisible, setQuickAddModalVisible] = useState(false);
  const openQuickAddDiscountCodeModal = () => {
    setQuickAddModalVisible(true);
  };
  const closeQuickAddDiscountCodeModal = () => {
    setQuickAddModalVisible(false);
  };

  const changeCellData = (index, key, newVal) => {
    const updatedTableData = [...tableData];
    updatedTableData[index] = {
      ...updatedTableData[index],
      [key]: newVal,
    };
    setTableData(updatedTableData);
  };

  // Calculate active and inactive counts
  const { activeCount, inactiveCount } = useMemo(() => {
    const active = tableData.filter(item => item.is_active === 1 || item.is_active === true).length;
    const inactive = tableData.filter(item => item.is_active === 0 || item.is_active === false).length;
    return { activeCount: active, inactiveCount: inactive };
  }, [tableData]);

  useEffect(()=>{
    const timeout = setTimeout(() => {
      // AsyncStorage.setItem('__search_options', JSON.stringify(searchOptions))
      // AsyncStorage.setItem('__search_options_timestamp', new Date().getTime().toString())
      setUpdateDiscountCodeTrigger(true)
    }, 300);
    return () => clearTimeout(timeout);
  }, [selectedBrand, selectedActive])

  useEffect(() => {
    if (updateDiscountCodeTrigger == true) getTable();
  }, [updateDiscountCodeTrigger]);

  const removeDiscountCode = (id) => {
    showConfirm(msgStr('deleteConfirmStr'), () => {
      deleteDiscountCode(id, (jsonRes, status, error) => {
        switch (status) {
          case 200:
            setUpdateDiscountCodeTrigger(true);
            showAlert('success', jsonRes.message);
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
    });
  };

  const getTable = () => {
    getDiscountCodesList({brand_id: parseInt(selectedBrand), is_active: parseInt(selectedActive) }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setUpdateDiscountCodeTrigger(false);
          setTableData(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
    getBrandsData((jsonRes, status, error) => {
      switch (status) {
            case 200:
              const sortedData = jsonRes
              .filter((item: any) => item.brand != null)
              .sort((a: any, b: any) => a.brand.localeCompare(b.brand));
        
              setBrandData(sortedData || []);
              break;
            case 500:
              showAlert('error', msgStr('serverError'));
              break;
            default:
              if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
              else showAlert('error', msgStr('unknownError'));
              break;
          }
        });
  };

  const blackList = useMemo(() => {
    return tableData.map((item) => item.code);
  }, [tableData]);

  const renderTableData = () => {
    const rows = [];
    if (tableData.length > 0) {
      tableData.map((item, index) => {
        rows.push(
          <View key={index} style={styles.tableRow}>
            <View style={[styles.cell]}>
              <Text>{item.code}</Text>
            </View>
            <View style={[styles.cell, { width: 200 }]}>
              {/* <Text>{item.valid_start_date}</Text> */}
              <Text>
                {new Date(item.valid_start_date).toLocaleString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
              </Text>
            </View>
            <View style={[styles.cell, { width: 200 }]}>
              {/* <Text>{item.valid_end_date}</Text> */}
              <Text>
                {new Date(item.valid_end_date).toLocaleString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
              </Text>
            </View>
            <View style={[styles.cell]}>
              <Text>{item.type === 1 ? 'Percentage' : item.type === 2 ? 'Flat fee' : ''}</Text>
            </View>
            <View style={[styles.cell, { alignItems: 'flex-end', width:100 }]}>
              <Text>{item.amount}</Text>
            </View>
            <View style={[styles.IconCell]}>
              <TouchableOpacity
                onPress={() => {
                  editDiscountCode(item);
                }}
              >
                <FontAwesome5 size={TextMediumSize} name="edit" color="black" />
              </TouchableOpacity>
            </View>
            <View style={[styles.IconCell]}>
              <TouchableOpacity
                onPress={() => {
                  removeDiscountCode(item.id);
                }}
              >
                <FontAwesome5 size={TextMediumSize} name="times" color="black" />
              </TouchableOpacity>
            </View>
          </View>
        );
      });
    } else {
      <></>;
    }
    return <>{rows}</>;
  };

  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => {
        openInventory(null);
      }}
      screenName={'Discount Codes'}
    >
      <ScrollView horizontal={true}>
        <View style={styles.container}>
          <View style={styles.toolbar}>
            <TouchableHighlight style={styles.button} onPress={openAddDiscountCodeModal}>
              <Text style={styles.buttonText}>Add</Text>
            </TouchableHighlight>
            <TouchableHighlight style={styles.button} onPress={openQuickAddDiscountCodeModal}>
              <Text style={styles.buttonText}>Quick Add</Text>
            </TouchableHighlight>
            <BOHTlbrSearchPicker
              width={125}
              items={[{label:'', value:''}, ...brandData.map((item, index)=>({'label':item.brand, 'value':item.id}))]}
              label="Brand"
              selectedValue={selectedBrand}
              onValueChange={(value) => setSelectedBrand(value)}
            />  
            <BOHTlbrSearchPicker
              width={125}
              items={[{label:'', value:''}, { label:'Yes', value: 1}, { label: 'No', value: 0 } ]}
              label="Active"
              selectedValue={selectedActive}
              onValueChange={(value) => setSelectedActive(value)}
            />  

            <Text style={{ marginLeft: 25, fontSize: TextdefaultSize }}>{`Total Active #: ${activeCount}`}</Text>
            <Text style={{ marginLeft: 25, fontSize: TextdefaultSize }}>{`Total Inactive #: ${inactiveCount}`}</Text>
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeader}>
              <Text style={[styles.columnHeader]}>{'Code'}</Text>
              <Text style={[styles.columnHeader, { width: 200 }]}>{'Valid From'}</Text>
              <Text style={[styles.columnHeader, { width: 200 }]}>{'Valid To'}</Text>
              <Text style={[styles.columnHeader]}>{'Type'}</Text>
              <Text style={[styles.columnHeader, { width: 100 }]}>{'Amount'}</Text>
              <Text style={[styles.columnHeader, styles.IconCell]}>{'Edit'}</Text>
              <Text style={[styles.columnHeader, styles.IconCell]}>{'DEL'}</Text>
            </View>
            <ScrollView style={{ flex: 1, maxHeight: screenHeight - 220 }}>
              {renderTableData()}
            </ScrollView>
          </View>
        </View>
      </ScrollView>

      <AddDiscountCodeModal
        isModalVisible={isAddModalVisible}
        DiscountCode={selectedDiscountCode}
        setUpdateDiscountCodeTrigger={setUpdateDiscountCodeTrigger}
        closeModal={closeAddDiscountCodeModal}
      />

      <QuickAddDiscountCodeModal
        isModalVisible={isQuickAddModalVisible}
        DiscountCode={selectedDiscountCode}
        BlackList={blackList}
        setUpdateDiscountCodeTrigger={setUpdateDiscountCodeTrigger}
        closeModal={closeQuickAddDiscountCodeModal}
      />
    </BasicLayout>
  );
};

const styles = discountCodesStyle;

export default DiscountCodes;